type MediaReferenceDelta = {
  addedKeys?: Iterable<string>;
  removedKeys?: Iterable<string>;
};

type MediaReferenceDeltaState = {
  addedKeys: string[];
  removedKeys: string[];
};

const STORAGE_PREFIX = 'diagram-media-reference-delta';
const MAX_KEYS_PER_DIAGRAM = 500;

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage ?? null;
}

function storageKey(diagramId: string): string {
  return `${STORAGE_PREFIX}:${diagramId}`;
}

function normalizeKeys(keys: Iterable<string> | undefined): string[] {
  const out = new Set<string>();
  for (const raw of keys ?? []) {
    if (typeof raw !== 'string') continue;
    const key = raw.trim();
    if (!key) continue;
    out.add(key);
  }
  return Array.from(out);
}

function mergeState(
  state: MediaReferenceDeltaState,
  delta: MediaReferenceDelta,
): MediaReferenceDeltaState {
  const added = new Set(state.addedKeys);
  const removed = new Set(state.removedKeys);

  for (const key of normalizeKeys(delta.addedKeys)) {
    added.add(key);
    removed.delete(key);
  }
  for (const key of normalizeKeys(delta.removedKeys)) {
    if (added.has(key)) {
      added.delete(key);
      continue;
    }
    removed.add(key);
  }

  return {
    addedKeys: Array.from(added).slice(0, MAX_KEYS_PER_DIAGRAM),
    removedKeys: Array.from(removed).slice(0, MAX_KEYS_PER_DIAGRAM),
  };
}

export function readDiagramMediaReferenceDeltaState(diagramId: string): MediaReferenceDeltaState {
  const id = diagramId.trim();
  if (!id) return { addedKeys: [], removedKeys: [] };
  const storage = getStorage();
  if (!storage) return { addedKeys: [], removedKeys: [] };
  const raw = storage.getItem(storageKey(id));
  if (!raw) return { addedKeys: [], removedKeys: [] };
  try {
    const parsed = JSON.parse(raw) as {
      addedKeys?: unknown;
      removedKeys?: unknown;
    };
    const addedKeys = Array.isArray(parsed.addedKeys)
      ? normalizeKeys(parsed.addedKeys as string[])
      : [];
    const removedKeys = Array.isArray(parsed.removedKeys)
      ? normalizeKeys(parsed.removedKeys as string[])
      : [];
    return mergeState({ addedKeys: [], removedKeys: [] }, { addedKeys, removedKeys });
  } catch {
    storage.removeItem(storageKey(id));
    return { addedKeys: [], removedKeys: [] };
  }
}

export function writeDiagramMediaReferenceDeltaState(
  diagramId: string,
  state: MediaReferenceDeltaState,
): void {
  const id = diagramId.trim();
  if (!id) return;
  const storage = getStorage();
  if (!storage) return;
  const normalized = mergeState({ addedKeys: [], removedKeys: [] }, state);
  if (normalized.addedKeys.length === 0 && normalized.removedKeys.length === 0) {
    storage.removeItem(storageKey(id));
    return;
  }
  storage.setItem(storageKey(id), JSON.stringify(normalized));
}

export function enqueueDiagramMediaReferenceDelta(
  diagramId: string,
  delta: MediaReferenceDelta,
): MediaReferenceDeltaState {
  const current = readDiagramMediaReferenceDeltaState(diagramId);
  const next = mergeState(current, delta);
  writeDiagramMediaReferenceDeltaState(diagramId, next);
  return next;
}

export function acknowledgeDiagramMediaReferenceDelta(
  diagramId: string,
  delta: MediaReferenceDelta,
): MediaReferenceDeltaState {
  const current = readDiagramMediaReferenceDeltaState(diagramId);
  const addedAck = new Set(normalizeKeys(delta.addedKeys));
  const removedAck = new Set(normalizeKeys(delta.removedKeys));
  const next = {
    addedKeys: current.addedKeys.filter((key) => !addedAck.has(key)),
    removedKeys: current.removedKeys.filter((key) => !removedAck.has(key)),
  };
  writeDiagramMediaReferenceDeltaState(diagramId, next);
  return next;
}
